/**
 * TextareaField - Multi-line text input with label and error handling
 *
 * Use with React Hook Form:
 * <TextareaField label="Description" error={errors.description?.message} {...register('description')} />
 */

import { forwardRef, type ReactNode } from 'react';

import { BaseTextarea, type BaseTextareaProps } from '../base';

export interface TextareaFieldProps extends Omit<BaseTextareaProps, 'hasError'> {
  /** Field label */
  label: string;
  /** Optional element shown next to the label */
  labelAccessory?: ReactNode;
  /** Error message from form validation */
  error?: string;
  /** Optional helper text shown below the textarea */
  hint?: string;
}

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  ({ label, labelAccessory, error, hint, className, ...props }, ref) => {
    return (
      <div className={className}>
        {labelAccessory ? (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-zinc-300">{label}</label>
            {labelAccessory}
          </div>
        ) : (
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
            {label}
          </label>
        )}
        <BaseTextarea ref={ref} hasError={!!error} className="mt-1" {...props} />
        {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {hint && !error && <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">{hint}</p>}
      </div>
    );
  }
);

TextareaField.displayName = 'TextareaField';
